// Edge/centre snapping against nearby objects (spec §5.3).
import type { Rect } from '../util/geometry.ts';

export type Axis = 'x' | 'y';
export interface Guide { x1: number; y1: number; x2: number; y2: number }
export interface SnapMatch { delta: number; target: number; rect: Rect }

function lines(r: Rect, axis: Axis): [number, number, number] {
  return axis === 'x' ? [r.x, r.x + r.w / 2, r.x + r.w] : [r.y, r.y + r.h / 2, r.y + r.h];
}

function best(values: readonly number[], candidates: readonly Rect[], axis: Axis, threshold: number): SnapMatch | null {
  let match: SnapMatch | null = null;
  for (const c of candidates) {
    for (const target of lines(c, axis)) {
      for (const v of values) {
        const delta = target - v;
        if (Math.abs(delta) <= threshold && (!match || Math.abs(delta) < Math.abs(match.delta))) {
          match = { delta, target, rect: c };
        }
      }
    }
  }
  return match;
}

/** A guide line through the snapped coordinate spanning both the subject and the target. */
export function guideFor(m: SnapMatch, axis: Axis, subject: Rect): Guide {
  if (axis === 'x') {
    const y1 = Math.min(subject.y, m.rect.y);
    const y2 = Math.max(subject.y + subject.h, m.rect.y + m.rect.h);
    return { x1: m.target, y1, x2: m.target, y2 };
  }
  const x1 = Math.min(subject.x, m.rect.x);
  const x2 = Math.max(subject.x + subject.w, m.rect.x + m.rect.w);
  return { x1, y1: m.target, x2, y2: m.target };
}

/** Snaps a moving rect's edges and centre on both axes. */
export function snapMove(r: Rect, candidates: readonly Rect[], threshold: number): { dx: number; dy: number; guides: Guide[] } {
  const mx = best(lines(r, 'x'), candidates, 'x', threshold);
  const my = best(lines(r, 'y'), candidates, 'y', threshold);
  const dx = mx?.delta ?? 0;
  const dy = my?.delta ?? 0;
  const moved = { ...r, x: r.x + dx, y: r.y + dy };
  const guides: Guide[] = [];
  if (mx) guides.push(guideFor(mx, 'x', moved));
  if (my) guides.push(guideFor(my, 'y', moved));
  return { dx, dy, guides };
}

/** Snaps a single coordinate (e.g. a resize edge). */
export function snapValue(value: number, candidates: readonly Rect[], axis: Axis, threshold: number): SnapMatch | null {
  return best([value], candidates, axis, threshold);
}
